import { DeliveryModel } from '@audibene/ta.utils-opportunity'
import { GlobalStyles } from '@mui/material'
import { registerLicense } from '@syncfusion/ej2-base'
import { Inject, PopupOpenEventArgs, ScheduleComponent, Week, WorkWeek } from '@syncfusion/ej2-react-schedule'
import { addMinutes } from 'date-fns'
import { getTimezoneOffset } from 'date-fns-tz'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { RRule } from 'rrule'
import { config } from '../../../config'
import { AppointmentType, AvailabilityType, Permission, RouteParameters } from '../../../constants'
import { useAuth, useAvailabilityConstraints, useFeatureFlags, useResource } from '../../../hooks'
import { theme } from '../../../modules/App/Providers/Theme/theme'
import { AvailabilityConstraint } from '../../../types'
import { appointmentTypeToDisplayText, deliveryModelToDisplayText } from '../../../util'
import { availabilityTheme, MINIMAL_TIME_BLOCK_IN_MINUTES, NEVER_FREQUENCY, RULE_FREQUENCY_DELIMITER } from '../constants'
import { ConstraintModal } from '../ConstraintModal'
import { DtoCalendarConstraint, SyncfusionCalendarEvent } from '../types'
import { getTheme } from '../utils'

if (config.SYNCFUSION_LICENSE_KEY) {
  registerLicense(config.SYNCFUSION_LICENSE_KEY)
}

const defaultConstraint: Partial<DtoCalendarConstraint> = {
  appointmentTypes: [],
  availabilityType: AvailabilityType.AVAILABLE,
  subject: '',
}

const syncfusionCalendarEventToDtoCalendarConstraint = ({
  appointmentTypes,
  AppointmentTypes,
  availabilityType,
  AvailabilityType,
  dateEnd,
  dateStart,
  deliveryModels,
  DeliveryModels,
  EndTime,
  EndTimezone,
  id,
  Id,
  isAllDay,
  IsAllDay,
  recurrenceFrequency,
  recurrenceException,
  RecurrenceException,
  recurrenceId,
  RecurrenceID,
  RecurrenceRule,
  StartTime,
  StartTimezone,
  subject,
  Subject,
}: Partial<SyncfusionCalendarEvent & DtoCalendarConstraint>): DtoCalendarConstraint => ({
  availabilityType: (AvailabilityType ?? availabilityType) as AvailabilityType,
  appointmentTypes: (AppointmentTypes ?? appointmentTypes) as AppointmentType[],
  dateEnd: (EndTime ?? dateEnd) as Date,
  dateEndTimeZone: EndTimezone as string,
  dateStart: (StartTime ?? dateStart) as Date,
  dateStartTimeZone: StartTimezone as string,
  deliveryModels: DeliveryModels ?? deliveryModels,
  id: Id ?? id,
  isAllDay: Boolean(IsAllDay ?? isAllDay),
  recurrenceException: RecurrenceException ?? recurrenceException,
  recurrenceId: RecurrenceID ?? recurrenceId,
  recurrenceFrequency: (RecurrenceRule ?? recurrenceFrequency) as string | undefined,
  subject: (Subject ?? subject) as string,
})

const availabilityConstraintToSyncfusionCalendarEvent = (constraint: AvailabilityConstraint): SyncfusionCalendarEvent => {
  const {
    availability_type,
    appointment_types = [] as AppointmentType[],
    delivery_models,
    duration_in_minutes,
    id,
    recurrence_rule,
    is_all_day,
  } = constraint
  const { options } = RRule.fromString(recurrence_rule)
  const { dtstart } = options
  // If tzid is not provided, then the corresponding constraint is invalid and needs to be addressed
  const timeZone = options.tzid as string
  const dateStart = new Date(dtstart.valueOf() - getTimezoneOffset(timeZone, dtstart))
  const dateEnd = addMinutes(dateStart, duration_in_minutes)
  const ruleFrequency = recurrence_rule ? recurrence_rule.split(RULE_FREQUENCY_DELIMITER)[1] : undefined
  const actualRuleFrequency = ruleFrequency === NEVER_FREQUENCY ? undefined : ruleFrequency

  const subject =
    availability_type === AvailabilityType.AVAILABLE
      ? appointment_types?.map((type) => appointmentTypeToDisplayText[type]).join(', ')
      : 'Unavailable'

  return {
    AppointmentTypes: appointment_types,
    AvailabilityType: availability_type,
    DeliveryModels: delivery_models,
    EndTime: dateEnd,
    EndTimezone: timeZone,
    Id: id,
    IsAllDay: is_all_day,
    RecurrenceRule: actualRuleFrequency,
    StartTime: dateStart,
    StartTimezone: timeZone,
    Subject: subject,
  }
}

const CALENDAR_SCROLL_POSITION_KEY = 'CALENDAR_SCROLL_POSITION'

const Calendar = () => {
  const scheduleRef = useRef<ScheduleComponent>(null)
  const { isLoading: isResourceLoading, resource } = useResource()
  const { t } = useTranslation()
  const { availabilityConstraints, isLoading: areAvailabilityConstraintsLoading } = useAvailabilityConstraints()
  const { user } = useAuth()
  const [isEditorOpen, beEditorOpen] = useState(false)
  const [isNew, beNew] = useState(false)
  const [constraint, setConstraint] = useState<DtoCalendarConstraint>()

  const userPermissions = user?.permissions ?? new Set<string>()
  const canManageAvailabilityConstraints = userPermissions.has(Permission.AVAILABILITY_CONSTRAINTS_WRITE)

  /* Shop level schedule management related*/
  const { [RouteParameters.SHOP_ID]: shopId, [RouteParameters.RESOURCE_ID]: resourceId } = useParams()
  const [isShopScheduleManagementFeatureFlagEnabled] = useFeatureFlags<boolean>(['ENABLE_SHOP_SCHEDULE_MANAGEMENT'])
  const [hasResourceId, setHasResourceId] = useState(false)
  const useShopScheduling = Boolean(shopId)
  const hasUserListVisibilityRole =
    useShopScheduling &&
    userPermissions.has(Permission.AVAILABILITY_CONSTRAINTS_DELETE) &&
    userPermissions.has(Permission.AVAILABILITY_CONSTRAINTS_WRITE)
  const shouldSeeResourceList = hasUserListVisibilityRole && isShopScheduleManagementFeatureFlagEnabled

  useEffect(() => {
    const notDefaultResourceId = resourceId && resourceId !== 'calendar'
    setHasResourceId(Boolean(notDefaultResourceId))
  }, [resourceId])
  /* Shop level schedule management related */

  useEffect(() => {
    if (!scheduleRef.current) return

    const calendarScrollContainer = scheduleRef.current.element.querySelector('.e-content-wrap')

    if (calendarScrollContainer) {
      const storedPosition = sessionStorage.getItem(CALENDAR_SCROLL_POSITION_KEY)
      const lastPosition = storedPosition ? Number(storedPosition) : 0

      calendarScrollContainer.scrollTo({ top: lastPosition })

      const listenerFn = () => sessionStorage.setItem(CALENDAR_SCROLL_POSITION_KEY, calendarScrollContainer?.scrollTop?.toString() ?? '0')
      const onScroll = () => window.requestAnimationFrame(listenerFn)

      calendarScrollContainer.addEventListener('scroll', onScroll)

      return () => calendarScrollContainer?.removeEventListener('scroll', onScroll)
    }
  }, [])

  const isReady = !(isResourceLoading || areAvailabilityConstraintsLoading)

  const onPopupOpen = (args: PopupOpenEventArgs) => {
    const { data: tmpData, element, target, type } = args
    const data = tmpData as SyncfusionCalendarEvent | DtoCalendarConstraint
    let merged = {} as DtoCalendarConstraint

    if (data) {
      merged = syncfusionCalendarEventToDtoCalendarConstraint({
        ...defaultConstraint,
        EndTimezone: resourceTimeZone as string,
        StartTimezone: resourceTimeZone as string,
        ...data,
      })
    }

    const isAvailabilityConstraint = Boolean(merged.availabilityType === AvailabilityType.AVAILABLE)

    if (type === 'QuickInfo') {
      beNew(!target?.classList?.contains('e-appointment'))

      if (!merged.recurrenceFrequency) {
        setConstraint(merged)
        beEditorOpen(true)
      }

      args.cancel = true

      return
    }

    if (type === 'DeleteAlert') {
      const titleEl = element.querySelector('#QuickDialog_title') as HTMLDivElement
      const contentEl = element.querySelector('#QuickDialog_dialog-content') as HTMLDivElement

      titleEl.innerText = isAvailabilityConstraint ? t('common.label.deleteAvailableTime') : t('common.label.deleteBlockedTime')

      contentEl.innerText = isAvailabilityConstraint
        ? t('common.message.deleteAvailableTimeConfirmation')
        : t('common.message.deleteBlockedTimeConfirmation')

      return
    }

    if (type === 'RecurrenceAlert') {
      const titleEl = element.querySelector('#QuickDialog_title') as HTMLDivElement
      const contentEl = element.querySelector('#QuickDialog_dialog-content') as HTMLDivElement
      const editEventButton = element.querySelector('button.e-quick-alertok') as HTMLButtonElement
      const editEntireSeriesButton = element.querySelector('button.e-quick-alertcancel') as HTMLButtonElement

      titleEl.innerText = isAvailabilityConstraint ? t('common.label.editAvailableTime') : t('common.label.editBlockedTime')

      contentEl.innerText = isAvailabilityConstraint
        ? t('pages.settings.resources.availabilityConstraints.changeAvailableTimeSeries')
        : t('pages.settings.resources.availabilityConstraints.changeBlockedTimeSeries')

      editEventButton.innerText = t('common.label.editOccurrence')

      // TODO: Allow single occurrence editing
      editEventButton.style.display = 'none'

      editEntireSeriesButton.innerText = t('common.label.editSeries')
      editEntireSeriesButton.click()

      args.cancel = true

      return
    }

    if (type === 'Editor') {
      // Use our custom modal instead of the built-in
      args.cancel = true

      setConstraint(merged)
      beEditorOpen(true)
    }
  }

  /**
   * Syncfusion calendar will swallow any errors in this function and
   * fail silenty, removing/not rendering the events.
   * The try/catch prevents that, so make sure all code is inside it.
   **/
  const onEventRendered = (args: any) => {
    try {
      const { element } = args
      const data = args?.data as SyncfusionCalendarEvent

      const availability = data?.AvailabilityType ?? undefined
      const appointmentTypes = data?.AppointmentTypes ?? []

      if (!element || !availability) {
        return
      }

      const theme = getTheme(availability, appointmentTypes)

      element.style.borderColor = `${theme.accent}33`
      element.style.backgroundColor = theme.background
      element.style.color = availabilityTheme[availability].foreground

      const subjectEl = element.querySelector('.e-subject') as HTMLDivElement
      const locationEl = element.querySelector('.e-location') as HTMLDivElement | null

      let tooltipTitle = ''
      let deliveryModelsText = ''
      let appointmentTypesText = ''

      if (data?.DeliveryModels?.length) {
        const deliveryModels = data.DeliveryModels as DeliveryModel[]
        deliveryModelsText += deliveryModels.map((deliveryModel) => deliveryModelToDisplayText[deliveryModel]).join(', ')
        tooltipTitle = `Delivery Models: ${deliveryModelsText}&#13;`
      }

      if (data?.AppointmentTypes?.length) {
        const appointmentTypes = data.AppointmentTypes as AppointmentType[]
        appointmentTypesText += appointmentTypes.map((type) => appointmentTypeToDisplayText[type]).join(', ')
        tooltipTitle += `Appointment Types: ${appointmentTypesText}`
      }

      // All day events don't have this element
      if (locationEl) {
        subjectEl.innerHTML = `<Tooltip placement="top" title="${tooltipTitle}"><span>${deliveryModelsText}<br>${appointmentTypesText}</span></Tooltip>`
        locationEl.style.display = 'none'
      }
    } catch (e) {
      console.error(e)
    }
  }

  const onEditorClose = () => beEditorOpen(false)

  if (!isReady) {
    return null
  }

  const resourceTimeZone = resource?.timezone
  const events = availabilityConstraints?.map(availabilityConstraintToSyncfusionCalendarEvent) ?? ([] as DtoCalendarConstraint[])

  /* Shop level schedule management related */
  if (shouldSeeResourceList) {
    if (!hasResourceId) {
      return <>Please select a resource to view their availability constraints.</>
    }
  }
  /* Shop level schedule management related */

  return (
    <>
      <GlobalStyles
        styles={{
          '.e-btn.e-primary': {
            backgroundColor: theme.palette.primary.light,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
            '&.e-active': {
              backgroundColor: theme.palette.primary.main,
            },
            '&.e-flat': {
              color: theme.palette.primary.main,
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: `${theme.palette.primary.main}0a`,
              },
            },
          },
          '.e-input-group.e-control-wrapper.e-valid-input.e-datetime-wrapper.e-input-focus::before': {
            backgroundColor: theme.palette.primary.main,
          },
          '.e-input-group.e-control-wrapper.e-valid-input.e-datetime-wrapper.e-input-focus::after': {
            backgroundColor: theme.palette.primary.main,
          },
          '.readonly-schedule .e-table-container': {
            pointerEvents: 'none',
          },
        }}
      />
      <ScheduleComponent
        key={canManageAvailabilityConstraints ? 'manageable' : 'readonly'}
        endHour="22:00"
        eventRendered={onEventRendered}
        eventSettings={{ dataSource: events }}
        height="800px"
        popupOpen={onPopupOpen}
        quickInfoOnSelectionEnd
        readOnly={!canManageAvailabilityConstraints}
        ref={scheduleRef}
        startHour="05:00"
        timeScale={{ interval: MINIMAL_TIME_BLOCK_IN_MINUTES * 2 }}
        timezone={resourceTimeZone}
        views={['Week', 'WorkWeek']}
        width="calc(100vw - 3rem)"
        workHours={{ highlight: true, start: '08:00', end: '18:00' }}
        className={canManageAvailabilityConstraints ? undefined : 'readonly-schedule'}
      >
        <Inject services={[Week, WorkWeek]} />
      </ScheduleComponent>
      <ConstraintModal constraint={constraint as DtoCalendarConstraint} isNew={isNew} isOpen={isEditorOpen} onClose={onEditorClose} />
    </>
  )
}

export { Calendar }
